import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import { DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule, JWT_OPTIONS } from '@auth0/angular-jwt';
import { NuMonacoEditorModule } from '@ng-util/monaco-editor';
import { JsonApiModule, JSON_API_URL, JSON_API_VERSION } from 'json-api';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { BLCInterceptor } from './core/http-interceptors/blc.interceptor';
import { ErrorInterceptor } from './core/http-interceptors/error.interceptor';
import { TokenService } from './core/token.service';
import { DashboardModule } from './dashboard/dashboard.module';
import { HomeModule } from './home/home.module';
import { TranslatePipe } from './translation/translate.pipe';
import { ErrorDialogComponent } from './core/http-interceptors/error-dialog/error-dialog.component';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DisplayDataPipe } from './resources/display-data.pipe';
import { MatMenuModule } from '@angular/material/menu';

registerLocaleData(localeFr, 'fr', localeFrExtra);

@NgModule({
  declarations: [AppComponent, ErrorDialogComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    ClipboardModule,
    HomeModule,
    AuthModule,
    DashboardModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressBarModule,
    JsonApiModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: (tokenService: TokenService) => ({
          tokenGetter: () => tokenService.get(),
          allowedDomains: environment.jwt.whitelist,
          disallowedRoutes: [/\/auth\/login$/, /\/auth\/refresh$/],
          skipWhenExpired: false,
        }),
        deps: [TokenService],
      },
    }),
    NuMonacoEditorModule.forRoot({
      baseUrl: `assets/monaco-editor/min`,
      defaultOptions: {
        theme: 'vs-dark',
        tabSize: 2,
        minimap: {
          enabled: false,
        },
      },
    }),
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BLCInterceptor,
      multi: true,
    },
    {
      provide: JSON_API_URL,
      useValue: environment.api,
    },
    {
      provide: JSON_API_VERSION,
      useValue: environment.version,
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { showError: true },
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'fr-FR',
    },
    TranslatePipe,
    DisplayDataPipe,
    DatePipe,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    iconRegistry
      .addSvgIcon(
        'atom',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/atom.svg')
      )
      .addSvgIcon(
        'atom-transparant',
        sanitizer.bypassSecurityTrustResourceUrl(
          'assets/icons/atom-transparant.svg'
        )
      )
      .addSvgIcon(
        'dashboard',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/dashboard.svg')
      )
      .addSvgIcon(
        'country',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/country.svg')
      )
      .addSvgIcon(
        'firm',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/firm.svg')
      )
      .addSvgIcon(
        'contract',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/contract.svg')
      )
      .addSvgIcon(
        'offer',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/offer.svg')
      )
      .addSvgIcon(
        'catalog',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/catalog.svg')
      )
      .addSvgIcon(
        'capping',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/capping.svg')
      )
      .addSvgIcon(
        'sku',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/sku.svg')
      )
      .addSvgIcon(
        'service',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/service.svg')
      )
      .addSvgIcon(
        'product',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/product.svg')
      )
      .addSvgIcon(
        'pack',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/pack.svg')
      )
      .addSvgIcon(
        'cascade',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/cascade.svg')
      )
      .addSvgIcon(
        'platform',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/platform.svg')
      )
      .addSvgIcon(
        'fee',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/fee.svg')
      )
      .addSvgIcon(
        'billing-policy',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/policy.svg')
      )
      .addSvgIcon(
        'tag',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/tag.svg')
      )
      .addSvgIcon(
        'acquisition-source',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/source.svg')
      )
      .addSvgIcon(
        'acquisition-account',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/account.svg')
      )
      .addSvgIcon(
        'traffic-manager',
        sanitizer.bypassSecurityTrustResourceUrl(
          'assets/icons/trafficmanager.svg'
        )
      )
      .addSvgIcon(
        'acquisition-mapping',
        sanitizer.bypassSecurityTrustResourceUrl('assets/icons/mapping.svg')
      )
      .addSvgIcon(
        'notifications',
        sanitizer.bypassSecurityTrustResourceUrl(
          'assets/icons/notifications.svg'
        )
      );
  }
}
